import React from 'react';
import {
  getLegBaggageRowsForBooking,
  getNonBaggageSsrRowsForBooking,
} from '../../../utils/ssrBreakdown';
import { fmtIST, fmtISTDate, fmtISTShort } from '../../../utils/datetimeIST';
import { resolvePassengerDisplayName } from '../../../utils/passengerDisplay';

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtDate = fmtIST;
const fmtDateOnly = fmtISTDate;

export function BookingReferencesPanel({ references }) {
  if (!references) return null;
  const rows = [
    ['ACF booking ID', references.acf_booking_id],
    ['Internal ID', references.internal_id],
    ['Supplier booking ID', references.supplier_booking_id],
    ['Tripjack ID', references.tripjack_booking_id],
    ['Riya track ID', references.riya_track_id],
    ['PNR', references.pnr],
    ['Riya PNR', references.riya_pnr],
    ['Return Riya PNR', references.return_riya_pnr],
  ].filter(([, v]) => v);
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="hub-references-panel">
      <p className="text-[10px] uppercase font-bold text-gray-500 mb-3">Booking references</p>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="bg-gray-50 rounded-lg px-3 py-2">
            <dt className="text-[10px] text-gray-500">{label}</dt>
            <dd className="font-mono font-medium text-gray-900 break-all">{value}</dd>
          </div>
        ))}
      </dl>
      {references.gds_pnrs?.length > 0 && (
        <p className="text-xs text-gray-600 mt-2">GDS PNRs: {references.gds_pnrs.join(', ')}</p>
      )}
      {references.ticket_numbers?.length > 0 && (
        <p className="text-xs text-gray-600 mt-1">Tickets: {references.ticket_numbers.join(', ')}</p>
      )}
    </div>
  );
}

export function StatusOverviewPanel({ statusOverview }) {
  if (!statusOverview?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="hub-status-overview">
      <p className="text-[10px] uppercase font-bold text-gray-500 mb-3">All statuses</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {statusOverview.map((row) => (
          <div key={row.label} className="flex justify-between gap-2 bg-gray-50 rounded-lg px-3 py-2 text-sm">
            <span className="text-gray-600">{row.label}</span>
            <span className="font-semibold text-gray-900 text-right">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function FlightItineraryPanel({ itinerary }) {
  const segments = itinerary?.segments || [];
  if (!segments.length) {
    return <p className="text-sm text-gray-500">No segment data available.</p>;
  }
  return (
    <div className="space-y-3" data-testid="hub-itinerary-panel">
      {segments.map((seg, idx) => {
        const o = seg.origin || {};
        const d = seg.destination || {};
        const mins = seg.duration_minutes;
        const dur = mins ? `${Math.floor(mins / 60)}h ${mins % 60}m` : null;
        return (
          <div key={idx} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold uppercase text-[#1B375B]">{seg.leg || 'segment'}</span>
              {seg.cabin_class && <span className="text-xs text-gray-500">{seg.cabin_class}</span>}
            </div>
            <p className="font-semibold text-gray-900">
              {seg.airline || seg.airline_code} {seg.flight_number}
            </p>
            <p className="text-lg font-bold text-[#1B375B] mt-1">
              {o.code} → {d.code}
              {(o.city || d.city) && (
                <span className="text-sm font-normal text-gray-600 ml-2">
                  {o.city}{d.city ? ` → ${d.city}` : ''}
                </span>
              )}
            </p>
            <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-gray-600">
              <span>Dep: {fmtISTShort(seg.departure_time)}</span>
              <span>Arr: {fmtISTShort(seg.arrival_time)}</span>
              {dur && <span>Duration: {dur}</span>}
              {seg.stops > 0 && <span>Stops: {seg.stops}</span>}
              {seg.baggage && <span>Check-in: {seg.baggage}</span>}
              {seg.cabin_baggage && <span>Cabin: {seg.cabin_baggage}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function PassengersDetailPanel({ passengers }) {
  if (!passengers?.length) {
    return <p className="text-sm text-gray-500">No passenger data.</p>;
  }
  return (
    <div className="space-y-2" data-testid="hub-passengers-panel">
      {passengers.map((p, idx) => (
        <div key={idx} className="border border-gray-200 rounded-xl p-4 bg-white">
          <p className="font-semibold text-gray-900">{resolvePassengerDisplayName(p)}</p>
          <p className="text-sm text-gray-600 mt-1">
            {[p.type, p.gender, p.dob && `DOB ${fmtDateOnly(p.dob)}`, p.nationality].filter(Boolean).join(' · ')}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2 text-xs">
            {p.pnr && <span><span className="text-gray-500">PNR:</span> {p.pnr}</span>}
            {p.ticket_number && <span><span className="text-gray-500">Ticket:</span> {p.ticket_number}</span>}
            {p.seat && <span><span className="text-gray-500">Seat:</span> {p.seat}</span>}
            {p.meal && <span><span className="text-gray-500">Meal:</span> {p.meal}</span>}
            {p.passport_number && <span><span className="text-gray-500">Passport:</span> {p.passport_number}</span>}
            {p.ff_number && <span><span className="text-gray-500">FF#:</span> {p.ff_number}</span>}
            {p.status && <span><span className="text-gray-500">Status:</span> {p.status}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

export function bookingForSsrView(booking, ssr, commercial) {
  return {
    ...(booking || {}),
    ssr_breakdown: ssr?.ssr_breakdown?.length ? ssr.ssr_breakdown : (commercial?.ssr_breakdown || booking?.ssr_breakdown),
    leg_baggage_summary: ssr?.leg_baggage_summary?.length ? ssr.leg_baggage_summary : (commercial?.leg_baggage_summary || booking?.leg_baggage_summary),
    ssr_amount: ssr?.ssr_amount ?? commercial?.ssr_amount ?? booking?.ssr_amount,
    seat_warning: ssr?.seat_warning || booking?.seat_warning,
    leg_route_labels: ssr?.leg_route_labels || booking?.leg_route_labels,
  };
}

export function SSRDetailPanel({ ssr, booking, commercial }) {
  const b = bookingForSsrView(booking, ssr, commercial);
  const legBag = getLegBaggageRowsForBooking(b);
  const nonBag = getNonBaggageSsrRowsForBooking(b);
  const rawRows = Array.isArray(b.ssr_breakdown) ? b.ssr_breakdown : [];
  const total = legBag.reduce((s, r) => s + Number(r.total_amount || 0), 0)
    + nonBag.reduce((s, r) => s + Number(r.amount || 0), 0)
    + rawRows.reduce((s, r) => s + Number(r.amount || 0), 0);
  const itemCount = legBag.length + nonBag.length || rawRows.length;
  if (!itemCount && !Number(b.ssr_amount || 0)) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-600">
        <p>No SSR / add-ons recorded on this booking.</p>
        <p className="text-xs mt-1 text-gray-500">If add-ons were purchased, ensure the backend is restarted after the latest hub update.</p>
      </div>
    );
  }
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2" data-testid="hub-ssr-panel">
      <p className="text-[10px] uppercase font-bold text-amber-900 mb-2">
        Add-ons &amp; SSR ({itemCount} item{itemCount === 1 ? '' : 's'} · {fmt(total || b.ssr_amount)})
      </p>
      {legBag.map((r, idx) => (
        <div key={`bag-${idx}`} className="flex justify-between text-sm">
          <span>🧳 {r.description || r.code} · {r.leg_label} · {r.pax_name || `Pax ${r.pax_ref}`}</span>
          <span className="font-semibold">{fmt(r.total_amount)}</span>
        </div>
      ))}
      {nonBag.map((row, idx) => {
        const icon = row.kind === 'meals' ? '🍽️' : row.kind === 'seats' ? '💺' : '✨';
        return (
          <div key={`nb-${idx}`} className="flex justify-between text-sm">
            <span>{icon} {row.description || row.code} · {row.leg} · {row.pax_name || `Pax ${row.pax_index}`}</span>
            <span className="font-semibold">{fmt(row.amount)}</span>
          </div>
        );
      })}
      {!legBag.length && !nonBag.length && rawRows.map((row, idx) => {
        const icon = row.kind === 'baggage' ? '🧳' : row.kind === 'meals' ? '🍽️' : row.kind === 'seats' ? '💺' : '✨';
        return (
          <div key={`raw-${idx}`} className="flex justify-between text-sm gap-3">
            <span>{icon} {row.description || row.code} · {row.pax_name || `Pax ${row.pax_index ?? row.pax_ref ?? '?'}`}</span>
            <span className="font-semibold">{fmt(row.amount)}</span>
          </div>
        );
      })}
      {b.seat_warning && (
        <p className="text-xs text-amber-900 italic border-t border-amber-200 pt-2">⚠️ {b.seat_warning}</p>
      )}
    </div>
  );
}

export function PaymentDetailPanel({ payment }) {
  if (!payment) return null;
  const wt = payment.wallet_transaction;
  const ct = payment.credit_transaction;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" data-testid="hub-payment-panel">
      <p className="text-[10px] uppercase font-bold text-gray-500">Payment & transaction</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
        <div className="bg-gray-50 rounded-lg p-2"><p className="text-[10px] text-gray-500">Mode</p><p className="font-semibold">{payment.payment_mode || '—'}</p></div>
        <div className="bg-gray-50 rounded-lg p-2"><p className="text-[10px] text-gray-500">Status</p><p className="font-semibold">{payment.payment_status || '—'}</p></div>
        <div className="bg-gray-50 rounded-lg p-2"><p className="text-[10px] text-gray-500">Total</p><p className="font-semibold">{fmt(payment.total_amount)}</p></div>
        <div className="bg-gray-50 rounded-lg p-2"><p className="text-[10px] text-gray-500">Paid</p><p className="font-semibold">{fmt(payment.amount_paid)}</p></div>
        {payment.paid_at && <div className="bg-gray-50 rounded-lg p-2"><p className="text-[10px] text-gray-500">Paid at</p><p className="font-semibold text-xs">{fmtDate(payment.paid_at)}</p></div>}
      </div>
      {payment.payment_transaction_id && (
        <p className="text-xs"><span className="text-gray-500">Transaction ID:</span> <span className="font-mono">{payment.payment_transaction_id}</span></p>
      )}
      {payment.order_id && (
        <p className="text-xs"><span className="text-gray-500">Gateway order:</span> <span className="font-mono">{payment.order_id}</span></p>
      )}
      {payment.pg_reference && (
        <p className="text-xs"><span className="text-gray-500">PG reference:</span> <span className="font-mono">{payment.pg_reference}</span></p>
      )}
      {wt && (
        <div className="border-t pt-3">
          <p className="text-xs font-bold text-gray-700 mb-1">Wallet transaction</p>
          <p className="text-xs text-gray-600">{wt.transaction_id} · {wt.type} · {fmt(wt.amount)} · {wt.status}</p>
        </div>
      )}
      {ct && (
        <div className="border-t pt-3">
          <p className="text-xs font-bold text-gray-700 mb-1">Credit transaction</p>
          <p className="text-xs text-gray-600">{ct.transaction_id} · {fmt(ct.amount)} · {ct.status}</p>
        </div>
      )}
      {(payment.gstin_on_booking || payment.billing_name) && (
        <div className="border-t pt-3 text-xs text-gray-600">
          {payment.billing_name && <p>Billing: {payment.billing_name}</p>}
          {payment.gstin_on_booking && <p>GSTIN: {payment.gstin_on_booking}</p>}
        </div>
      )}
    </div>
  );
}

export function GstInvoicePanel({ gst }) {
  if (!gst) return null;
  const has = gst.gst_invoice_number || gst.gst_invoice_id;
  const deferred = gst.gst_invoice_deferred_reason;
  const deferredLabels = {
    supplier_pending: 'Pending supplier confirmation',
    supplier_not_confirmed: 'Supplier not confirmed',
    payment_not_settled: 'Payment not settled',
    booking_on_hold: 'Booking on hold',
    agent_without_gstin_walkin: 'Agent GST profile incomplete',
    invalid_agent_gstin: 'Invalid agent GSTIN',
    legacy_no_markup_capture: 'No markup captured on booking',
    no_invoice: 'Not yet issued',
  };
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="hub-gst-panel">
      <p className="text-[10px] uppercase font-bold text-gray-500 mb-3">GST invoice</p>
      {!has ? (
        <div className="text-sm text-gray-500 space-y-1">
          <p>No GST invoice linked to this booking.</p>
          {deferred && (
            <p className="text-amber-700 text-xs" data-testid="hub-gst-deferred-reason">
              Deferred: {deferredLabels[deferred] || deferred.replace(/_/g, ' ')}
              {gst.gst_invoice_deferred_at ? ` · ${fmtDate(gst.gst_invoice_deferred_at)}` : ''}
            </p>
          )}
        </div>
      ) : (
        <dl className="text-sm space-y-1">
          <div><dt className="text-gray-500 inline">Invoice # </dt><dd className="inline font-mono font-semibold">{gst.gst_invoice_number || '—'}</dd></div>
          {gst.gst_invoice_id && <div><dt className="text-gray-500 inline">ID </dt><dd className="inline font-mono">{gst.gst_invoice_id}</dd></div>}
          {gst.invoice_status && <div><dt className="text-gray-500 inline">Status </dt><dd className="inline">{gst.invoice_status}</dd></div>}
          {gst.invoice_created_at && <div><dt className="text-gray-500 inline">Issued </dt><dd className="inline">{fmtDate(gst.invoice_created_at)}</dd></div>}
          {gst.invoice_total != null && <div><dt className="text-gray-500 inline">Amount </dt><dd className="inline">{fmt(gst.invoice_total)}</dd></div>}
          {gst.download_path && (
            <a href={`${process.env.REACT_APP_BACKEND_URL}${gst.download_path}`} className="inline-block mt-2 text-sm text-blue-600 font-semibold" target="_blank" rel="noreferrer">
              Download invoice
            </a>
          )}
        </dl>
      )}
    </div>
  );
}

export function CustomerSummaryPanel({ summary }) {
  if (!summary) return null;
  const isAgent = summary.user_type === 'agent';
  const label = isAgent ? 'Agent company' : 'B2C client';
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm" data-testid="hub-customer-summary">
      <p className="text-[10px] uppercase font-bold text-gray-500 mb-2">{label}</p>
      {summary.customer_name && <p className="font-semibold text-gray-900">{summary.customer_name}</p>}
      <p className="text-gray-600">{summary.customer_email || '—'}</p>
      {summary.customer_phone && <p className="text-gray-600">{summary.customer_phone}</p>}
      {summary.user_type && (
        <span className="inline-block mt-2 text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-white border text-gray-600">
          {isAgent ? 'Agent' : 'B2C'}
        </span>
      )}
    </div>
  );
}
